import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { blogPosts } from '../../../mocks/blogPosts';
import { useLazyImage } from '../../../hooks/useLazyImage';
import ArticleTableOfContents, { TocItem } from './ArticleTableOfContents';

// Lazy image wrapper component for related articles
function LazyRelatedImage({ src, alt, title }: { src: string; alt: string; title: string }) {
  const { ref, isVisible } = useLazyImage({ rootMargin: '200px' });
  
  return (
    <div ref={ref} className="w-20 h-16 sm:w-24 sm:h-20 rounded-lg overflow-hidden flex-shrink-0">
      {isVisible ? (
        <img
          src={src}
          alt={alt}
          title={title}
          width={96}
          height={80}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <div className="w-full h-full bg-gray-100 animate-pulse" />
      )}
    </div>
  );
}

export default function ArticleContent() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === slug);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleCopyLink = () => {
    const url = `https://rockhoundauto.com/blog/${post?.slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!post) {
    return (
      <div className="pt-32 pb-20 text-center px-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Article Not Found</h2>
        <p className="text-gray-500 mb-6">The article you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/blog" className="text-teal-600 font-medium hover:underline cursor-pointer">
          &larr; Back to Blog
        </Link>
      </div>
    );
  }

  // Build TOC from heading sections — safe if sections is absent
  const sections = (post as any).sections || [];
  const tocItems: TocItem[] = sections
    .map((section: any, index: number) => ({ section, index }))
    .filter(({ section }: any) => section.type === 'heading')
    .map(({ section, index }: any, tocIndex: number) => ({
      id: `section-heading-${index}`,
      text: section.content as string,
      index: tocIndex,
    }));

  const articleUrl = encodeURIComponent(`https://rockhoundauto.com/blog/${post.slug}`);
  const articleTitle = encodeURIComponent(post.title);
  const otherPosts = blogPosts.filter(p => p.slug !== slug);

  // Extract key takeaways from the article sections
  const extractKeyTakeaways = () => {
    const takeaways: string[] = [];
    for (const section of sections.slice(0, 5)) {
      if (section.type === 'list' && 'items' in section) {
        takeaways.push(...section.items.slice(0, 4));
        if (takeaways.length >= 4) break;
      }
    }
    if (takeaways.length < 3) {
      return [
        `Comprehensive guide to ${post.title.toLowerCase().replace(/^(how to|what is|why|when|where)/i, '').trim()}`,
        'Expert insights from over 15 years of automotive industry experience',
        'Real-world examples and pricing data from the Pacific Northwest market',
        'Actionable strategies to save thousands on your next vehicle purchase'
      ];
    }
    return takeaways.slice(0, 4);
  };

  const renderSection = (section: (typeof post.sections)[number], index: number) => {
    switch (section.type) {
      case 'intro':
        return (
          <p key={index} className="text-base text-gray-700 leading-relaxed mb-8 first-letter:text-3xl first-letter:font-bold first-letter:text-teal-600 first-letter:mr-1">
            {section.content}
          </p>
        );
      case 'heading':
        return (
          <h2
            key={index}
            id={`section-heading-${index}`}
            className="text-xl font-bold text-gray-900 mt-10 mb-4 scroll-mt-28"
          >
            <strong>{section.content}</strong>
          </h2>
        );
      case 'subheading':
        return (
          <h3 key={index} className="text-lg font-semibold text-gray-800 mt-6 mb-3">
            {section.content}
          </h3>
        );
      case 'paragraph':
        return (
          <p key={index} className="text-sm text-gray-600 leading-relaxed mb-5">
            {section.content}
          </p>
        );
      case 'quote':
        return (
          <blockquote key={index} className="border-l-4 border-teal-500 bg-teal-50/50 pl-5 pr-4 py-4 my-8 rounded-r-lg">
            <p className="text-base italic text-gray-700 leading-relaxed">
              &ldquo;{section.content}&rdquo;
            </p>
          </blockquote>
        );
      case 'list':
        return (
          <ul key={index} className="space-y-3 my-6 ml-1">
            {(section as { type: 'list'; items: string[] }).items.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-600 leading-relaxed">
                <span className="w-2 h-2 bg-teal-500 rounded-full mt-1.5 flex-shrink-0"></span>
                {item}
              </li>
            ))}
          </ul>
        );
      default:
        return null;
    }
  };

  const MidArticleConsignmentCta = () => (
    <div className="my-10 relative overflow-hidden bg-gradient-to-br from-stone-800 to-stone-900 rounded-xl p-6 sm:p-8 text-center">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-64 h-64 bg-teal-400 rounded-full -translate-y-1/2 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-400 rounded-full translate-y-1/3 -translate-x-1/4"></div>
      </div>
      <div className="relative z-10">
        <span className="inline-block bg-teal-500/20 text-teal-400 text-xs font-semibold px-3 py-1 rounded-full mb-3">
          Skip the Hassle
        </span>
        <h3 className="text-lg font-bold text-white mb-2">Why Not Let Us Handle It?</h3>
        <p className="text-sm text-stone-300 mb-5 max-w-md mx-auto leading-relaxed">
          As you can see, consignment consistently nets sellers more money with a fraction of the effort. Ready to see what your vehicle is worth?
        </p>
        <Link
          to="/consignment#consignment-form"
          className="inline-flex items-center gap-2 bg-teal-500 text-white px-7 py-2.5 rounded-full text-sm font-bold hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
        >
          Start Your Consignment
          <i className="ri-arrow-right-line"></i>
        </Link>
        <p className="text-xs text-stone-500 mt-3">Free vehicle evaluation · No obligation · (855) 468-6348</p>
      </div>
    </div>
  );

  return (
    <div className="pt-16 lg:pt-20 pb-16 sm:pb-16 pb-24">
      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-4">
        <nav className="flex items-center gap-1.5 text-sm text-gray-400 flex-wrap">
          <Link to="/" className="hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
          <Link to="/blog" className="hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Blog</Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
          <span className="text-gray-600 truncate max-w-[160px] sm:max-w-xs">{post.title}</span>
        </nav>
      </div>

      {/* Hero Image */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-6 sm:mb-8">
        {/*
          hero-img-article:
          - aspect-ratio: 3/1 on mobile → 16/5 on sm+ → 3/1 on lg+
          - Browser reserves exact space before image loads → zero CLS
          - overflow-hidden + rounded-xl clip the image
        */}
        <div className="hero-img-article w-full rounded-xl overflow-hidden">
          <img
            src={(post as any).heroImage || (post as any).image || ''}
            alt={`${post.title} - Rockhound Auto Warrenton Oregon`}
            title={`${post.title} - Rockhound Auto Warrenton Oregon`}
            width={1200}
            height={400}
            className="w-full h-full object-cover object-top"
            loading="eager"
            fetchPriority="high"
            decoding="sync"
          />
        </div>
      </div>

      {/* Article Header */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 mb-8 sm:mb-10">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
          <span className="bg-teal-600 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
            {post.category}
          </span>
          <span className="text-xs text-gray-400 flex items-center gap-1 whitespace-nowrap">
            <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
            {post.date}
          </span>
          <span className="text-xs text-gray-400 flex items-center gap-1 whitespace-nowrap">
            <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center"></i>
            {post.readTime}
          </span>
        </div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-4">
          {post.title}
        </h1>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-user-line text-teal-600 text-sm"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{post.author}</p>
            <p className="text-xs text-gray-400">Warrenton, Oregon</p>
          </div>
        </div>
      </div>

      {/* Article Summary */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 mb-8">
        <div className="bg-stone-50 border-l-4 border-teal-500 rounded-r-lg p-5 sm:p-6">
          <h3 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
            <i className="ri-file-text-line text-teal-600" aria-hidden="true"></i>
            Article Summary
          </h3>
          <p className="text-sm text-gray-700 leading-relaxed">
            {post.excerpt}
          </p>
        </div>
      </div>

      {/* Key Takeaways */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 mb-8">
        <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-xl p-5 sm:p-6 border border-teal-200">
          <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-lightbulb-line text-teal-600 text-lg" aria-hidden="true"></i>
            Key Takeaways
          </h3>
          <ul className="space-y-3">
            {extractKeyTakeaways().map((takeaway, index) => (
              <li key={index} className="flex items-start gap-3 text-sm text-gray-700 leading-relaxed">
                <i className="ri-checkbox-circle-fill text-teal-600 text-base mt-0.5 flex-shrink-0"></i>
                <span>{takeaway}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Two-column layout: TOC sidebar + Article body ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-10 items-start">

        {/* TOC Sidebar */}
        {tocItems.length >= 2 && (
          <ArticleTableOfContents items={tocItems} />
        )}

        {/* Article Body */}
        <div className="flex-1 min-w-0">
          <article className="max-w-3xl">
            {sections.length > 0 ? sections.map((section: any, index: number) => {
              const rendered = renderSection(section, index);
              const showMidCta =
                (post as any).id === 'consignment-vs-private-selling-which-is-better' &&
                section.type === 'paragraph' &&
                typeof section.content === 'string' &&
                section.content.includes('Mike netted $1,550 more');
              return (
                <div key={`section-wrapper-${index}`}>
                  {rendered}
                  {showMidCta && <MidArticleConsignmentCta />}
                </div>
              );
            }) : (
              // Fallback: render excerpt when no sections exist yet
              <p className="text-base text-gray-700 leading-relaxed mb-8 first-letter:text-3xl first-letter:font-bold first-letter:text-teal-600 first-letter:mr-1">
                {post.excerpt}
              </p>
            )}
          </article>

          {/* About This Guide */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="bg-white rounded-xl p-5 sm:p-6 border border-gray-100">
              <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-information-line text-teal-600 text-lg" aria-hidden="true"></i>
                About This Guide
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                This guide was written by the Rockhound Auto team, drawing on over 15 years of hands-on experience in the automotive industry across the Pacific Northwest. Our team includes licensed auto brokers, former dealership finance managers, and automotive journalists who have helped thousands of buyers navigate the car-buying process in Oregon, Washington, Idaho, and beyond.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                We specialize in helping buyers find the right vehicle at the right price — without the pressure tactics, hidden fees, or inflated markups that plague traditional dealerships. Whether you're shopping for a used truck in Bend, a family SUV in Seattle, or an EV in Portland, we provide honest, transparent guidance backed by real market data and wholesale pricing access.
              </p>
              <div className="flex flex-wrap gap-3 mt-5">
                <Link to="/broker-services" className="inline-flex items-center gap-2 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors cursor-pointer">
                  Learn About Our Broker Services <i className="ri-arrow-right-line"></i>
                </Link>
                <Link to="/inventory" className="inline-flex items-center gap-2 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors cursor-pointer">
                  Browse Current Inventory <i className="ri-arrow-right-line"></i>
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors cursor-pointer">
                  Contact Our Team <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Consignment CTA */}
      {(post as any).id === 'consignment-vs-private-selling-which-is-better' && (
        <div className="max-w-3xl mt-10">
          <div className="relative overflow-hidden bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-5 sm:p-8 text-center">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white rounded-full"></div>
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white rounded-full"></div>
            </div>
            <div className="relative z-10">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-car-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Ready to Consign Your Vehicle?</h3>
              <p className="text-sm text-teal-100 mb-5 sm:mb-6 max-w-lg mx-auto">
                Skip the hassle of private selling. Let Rockhound Auto handle everything — from reconditioning to closing the deal.
              </p>
              <Link
                to="/consignment#consignment-form"
                className="inline-flex items-center gap-2 bg-white text-teal-700 px-6 sm:px-8 py-3 rounded-full text-sm font-bold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
              >
                Start Your Consignment <i className="ri-arrow-right-line"></i>
              </Link>
              <p className="text-xs text-teal-200 mt-4">No obligation · Free vehicle evaluation · Call (855) 468-6348</p>
            </div>
          </div>
        </div>
      )}

      {/* Tags */}
      <div className="max-w-3xl mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-gray-100">
        <div className="flex flex-wrap gap-2">
          {post.tags.map((tag) => (
            <span key={tag} className="text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Social Sharing Bar */}
      <div className="max-w-3xl mt-6 sm:mt-8">
        <div className="bg-stone-50 rounded-xl p-4 sm:p-6">
          <p className="text-sm font-semibold text-gray-900 mb-3 sm:mb-4">Share This Article</p>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${articleUrl}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-1.5 bg-[#1877F2] text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs font-medium hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              aria-label="Share on Facebook"
            >
              <i className="ri-facebook-fill text-sm"></i>
              <span className="hidden xs:inline sm:inline">Facebook</span>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${articleTitle}&url=${articleUrl}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-1.5 bg-gray-900 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs font-medium hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              aria-label="Share on X"
            >
              <i className="ri-twitter-x-fill text-sm"></i>
              <span>X</span>
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${articleUrl}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-1.5 bg-[#0A66C2] text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs font-medium hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              aria-label="Share on LinkedIn"
            >
              <i className="ri-linkedin-fill text-sm"></i>
              <span className="hidden sm:inline">LinkedIn</span>
            </a>
            <a
              href={`https://pinterest.com/pin/create/button/?url=${articleUrl}&description=${articleTitle}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-1.5 bg-[#E60023] text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs font-medium hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              aria-label="Share on Pinterest"
            >
              <i className="ri-pinterest-fill text-sm"></i>
              <span className="hidden sm:inline">Pinterest</span>
            </a>
            <a
              href={`mailto:?subject=${articleTitle}&body=Check out this article from Rockhound Auto: ${articleUrl}`}
              className="flex items-center gap-1.5 bg-teal-600 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs font-medium hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              aria-label="Share via Email"
            >
              <i className="ri-mail-send-line text-sm"></i>
              <span className="hidden sm:inline">Email</span>
            </a>
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              aria-label="Copy link"
            >
              <i className={`${copied ? 'ri-check-line text-teal-600' : 'ri-link'} text-sm`}></i>
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-3xl mt-10 sm:mt-12">
        <div className="bg-stone-50 rounded-xl p-5 sm:p-8 text-center">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Ready to Find Your Next Vehicle?</h3>
          <p className="text-sm text-gray-500 mb-4 sm:mb-5 max-w-md mx-auto">
            Rockhound Auto serves Warrenton, Astoria, Seaside, and the entire Pacific Northwest. Let us find your perfect car.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/inventory"
              className="w-full sm:w-auto bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-center"
            >
              Browse Inventory
            </Link>
            <a
              href="tel:855-468-6348"
              className="w-full sm:w-auto bg-gray-900 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-center"
            >
              Call (855) 468-6348
            </a>
          </div>
        </div>
      </div>

      {/* Related Articles */}
      {otherPosts.length > 0 && (
        <div className="max-w-3xl mt-12 sm:mt-16 pb-8 sm:pb-0">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">More Articles</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {otherPosts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                aria-label={`Read article: ${p.title}`}
                className="group flex gap-3 sm:gap-4 bg-white rounded-lg border border-gray-100 p-3 sm:p-4 hover:shadow-md hover:border-gray-200 transition-all text-left cursor-pointer w-full"
              >
                <LazyRelatedImage
                  src={(p as any).heroImage || (p as any).image || ''}
                  alt={`${p.title} — ${p.category} article`}
                  title={`${p.title} - Rockhound Auto Blog`}
                />
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-teal-600 font-medium">{p.category}</span>
                  <h4 className="text-xs sm:text-sm font-bold text-gray-900 mt-0.5 sm:mt-1 line-clamp-2 group-hover:text-teal-600 transition-colors leading-snug">
                    {p.title}
                  </h4>
                  <span className="text-xs text-gray-400 mt-1 block">{p.readTime}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}